// Eyes-free answer capture. Listens to the same mic the call uses, cuts out each utterance
// with a simple voice detector, and hands a 16 kHz WAV clip to the server for transcription.
// Raw PCM keeps it identical across Chrome and Safari, whose MediaRecorder formats differ.

const WORKLET = `
class Tap extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch) this.port.postMessage(ch.slice(0));
    return true;
  }
}
registerProcessor("ml-tap", Tap);
`;

export interface VoiceOptions {
  onClip: (wav: Blob) => void;
  onSpeechStart?: () => void;
  onLevel?: (rms: number, speaking: boolean) => void;
}

export class VoiceCapture {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private frames: Float32Array[] = [];
  private pre: Float32Array[] = [];
  private speaking = false;
  private silentMs = 0;
  private speechMs = 0;
  private floor = 0.004;
  private carry = new Float32Array(0);
  private frameSize = 0;
  private started = 0;
  private received = 0;
  private enabled = true;

  constructor(private stream: MediaStream, private opts: VoiceOptions) {}

  async start(): Promise<void> {
    const ctx = new AudioContext();
    this.ctx = ctx;
    if (ctx.state === "suspended") {
      await Promise.race([ctx.resume().catch(() => {}), new Promise((r) => setTimeout(r, 300))]);
      // iOS may refuse until the next tap.
      const retry = () => { void ctx.resume(); };
      addEventListener("pointerdown", retry, { once: true });
    }
    const url = URL.createObjectURL(new Blob([WORKLET], { type: "text/javascript" }));
    await ctx.audioWorklet.addModule(url);
    URL.revokeObjectURL(url);
    this.source = ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(ctx, "ml-tap");
    this.frameSize = Math.round(ctx.sampleRate * 0.02);
    this.started = performance.now();
    this.received = 0;
    this.node.port.onmessage = (e) => this.feed(e.data as Float32Array);
    const silent = ctx.createGain();
    silent.gain.value = 0;
    this.source.connect(this.node).connect(silent).connect(ctx.destination);
    // Chrome throttles an AudioContext after 30 seconds of all-zero output, which drops about 75%
    // of the audio and garbles speech. Measured 2026-11-04: delivery fell to 0.26 at 30s without
    // this and held at 1.00 for 70s with it. At -100 dB, this signal is inaudible.
    const hum = ctx.createConstantSource();
    const quiet = ctx.createGain();
    quiet.gain.value = 1e-5;
    hum.connect(quiet).connect(ctx.destination);
    hum.start();
  }

  /** Turned off while the app itself is talking, so it doesn't answer itself. */
  setEnabled(on: boolean): void {
    if (this.enabled && !on) this.reset();
    this.enabled = on;
  }

  private reset(): void {
    this.speaking = false;
    this.frames = [];
    this.silentMs = 0;
    this.speechMs = 0;
  }

  private feed(chunk: Float32Array): void {
    this.received += chunk.length;
    const joined = new Float32Array(this.carry.length + chunk.length);
    joined.set(this.carry);
    joined.set(chunk, this.carry.length);
    let at = 0;
    while (joined.length - at >= this.frameSize) {
      this.frame(joined.slice(at, at + this.frameSize));
      at += this.frameSize;
    }
    this.carry = joined.slice(at);
  }

  /** Share of expected audio that actually arrived. Well under 1 means the machine is dropping audio. */
  deliveryRatio(): number {
    if (!this.ctx || !this.started) return 1;
    const seconds = (performance.now() - this.started) / 1000;
    return seconds > 1 ? this.received / (seconds * this.ctx.sampleRate) : 1;
  }

  private frame(frame: Float32Array): void {
    let sum = 0;
    for (const v of frame) sum += v * v;
    const rms = Math.sqrt(sum / frame.length);
    const threshold = Math.max(0.012, this.floor * 3.2);
    const loud = rms > threshold;
    // Tracks room noise only: speech mustn't raise the bar, or later words get clipped.
    if (rms < this.floor) this.floor = this.floor * 0.9 + rms * 0.1;
    else if (!loud && !this.speaking) this.floor = this.floor * 0.995 + rms * 0.005;
    this.opts.onLevel?.(rms, this.speaking);

    if (!this.enabled) {
      this.pre = [];
      return;
    }

    if (!this.speaking) {
      this.pre.push(frame);
      if (this.pre.length > 15) this.pre.shift(); // 300 ms pre-roll so first consonants survive.
      if (loud) {
        this.speaking = true;
        this.opts.onSpeechStart?.();
        this.frames = this.pre.slice();
        this.pre = [];
        this.silentMs = 0;
        this.speechMs = 20;
      }
      return;
    }

    this.frames.push(frame);
    if (loud) { this.speechMs += 20; this.silentMs = 0; } else { this.silentMs += 20; }
    const totalMs = this.frames.length * 20;
    if (this.silentMs >= 420 || totalMs >= 4000) {
      const keep = this.speechMs >= 180; // Coughs and clicks are shorter than a word.
      const frames = this.frames;
      this.reset();
      if (keep && this.ctx) this.opts.onClip(encodeWav(frames, this.ctx.sampleRate));
    }
  }

  stop(): void {
    this.node?.disconnect();
    this.source?.disconnect();
    void this.ctx?.close();
    this.ctx = null;
  }
}

function encodeWav(frames: Float32Array[], rate: number): Blob {
  const target = 16000;
  const ratio = rate / target;
  const total = frames.reduce((a, f) => a + f.length, 0);
  const flat = new Float32Array(total);
  let o = 0;
  for (const f of frames) { flat.set(f, o); o += f.length; }
  const outLen = Math.floor(total / ratio);
  const out = new Int16Array(outLen);
  let peak = 0;
  for (const v of flat) peak = Math.max(peak, Math.abs(v));
  const gain = peak > 0 ? Math.min(4, 0.9 / peak) : 1;
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio), end = Math.max(start + 1, Math.floor((i + 1) * ratio));
    let s = 0;
    for (let j = start; j < end && j < total; j++) s += flat[j]!;
    const v = (s / (end - start)) * gain;
    out[i] = Math.max(-32768, Math.min(32767, Math.round(v * 32767)));
  }
  const header = new ArrayBuffer(44);
  const d = new DataView(header);
  const str = (off: number, s: string) => { for (let i = 0; i < s.length; i++) d.setUint8(off + i, s.charCodeAt(i)); };
  str(0, "RIFF"); d.setUint32(4, 36 + out.byteLength, true); str(8, "WAVE"); str(12, "fmt ");
  d.setUint32(16, 16, true); d.setUint16(20, 1, true); d.setUint16(22, 1, true);
  d.setUint32(24, target, true); d.setUint32(28, target * 2, true); d.setUint16(32, 2, true); d.setUint16(34, 16, true);
  str(36, "data"); d.setUint32(40, out.byteLength, true);
  return new Blob([header, out.buffer as ArrayBuffer], { type: "audio/wav" });
}
